import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, 
  Users, 
  FileText, 
  CheckCircle2, 
  Clock,
  Download,
  Eye,
  HardDrive,
  AlertTriangle,
  Trash2,
  AlertCircle,
  FolderDown,
  Globe,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  // Calculate default month: previous month
  const getDefaultMonth = () => {
    if (currentMonth === 1) {
      return 12; // December
    }
    return currentMonth - 1;
  };
  
  const getDefaultYear = () => {
    if (currentMonth === 1) {
      return currentYear - 1; // Previous year if we're in January
    }
    return currentYear;
  };
  
  const [selectedYear, setSelectedYear] = useState(getDefaultYear());
  const [selectedMonth, setSelectedMonth] = useState(getDefaultMonth());
  const [validYears, setValidYears] = useState([currentYear - 1, currentYear]);
  const [usersStats, setUsersStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Storage stats
  const [storageStats, setStorageStats] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(true);
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, year: null, month: null });
  const [deleting, setDeleting] = useState(false);
  
  // Document detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDocuments, setUserDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [viewingFattureEstereOnly, setViewingFattureEstereOnly] = useState(false);
  
  // Delete single document dialog
  const [deleteDocDialog, setDeleteDocDialog] = useState({ open: false, doc: null });
  const [deletingDoc, setDeletingDoc] = useState(false);
  
  // Download all documents
  const [downloadingAll, setDownloadingAll] = useState(false);
  
  // Download month ZIP
  const [downloadingMonthZip, setDownloadingMonthZip] = useState(false);
  
  // Fatture estere pending
  const [fattureEsterePending, setFattureEsterePending] = useState(null);
  const [loadingFattureEstere, setLoadingFattureEstere] = useState(true);
  const [fattureEstereExpanded, setFattureEstereExpanded] = useState(true);

  useEffect(() => {
    fetchValidYears();
    fetchStorageStats();
    fetchFattureEsterePending();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedYear, selectedMonth]);

  const fetchValidYears = async () => {
    try {
      const response = await axios.get(`${API}/years`, {
        headers: getAuthHeader()
      });
      setValidYears(response.data.years);
    } catch (error) {
      setValidYears([currentYear - 1, currentYear]);
    }
  };

  const fetchStorageStats = async () => {
    setLoadingStorage(true);
    try {
      const response = await axios.get(`${API}/admin/storage-stats`, {
        headers: getAuthHeader()
      });
      setStorageStats(response.data);
    } catch (error) {
      console.error("Error fetching storage stats:", error);
    } finally {
      setLoadingStorage(false);
    }
  };

  const fetchFattureEsterePending = async () => {
    setLoadingFattureEstere(true);
    try {
      const response = await axios.get(`${API}/admin/fatture-estere-pending`, {
        headers: getAuthHeader()
      });
      setFattureEsterePending(response.data);
    } catch (error) {
      console.error("Error fetching fatture estere:", error);
    } finally {
      setLoadingFattureEstere(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/dashboard?year=${selectedYear}&month=${selectedMonth}`,
        { headers: getAuthHeader() }
      );
      setUsersStats(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const openUserDocuments = async (user) => {
    setSelectedUser(user);
    setLoadingDocs(true);
    setViewingFattureEstereOnly(false); // Viewing all documents
    try {
      const response = await axios.get(
        `${API}/admin/users/${user.user.id}/documents/${selectedYear}/${selectedMonth}`,
        { headers: getAuthHeader() }
      );
      setUserDocuments(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento documenti");
    } finally {
      setLoadingDocs(false);
    }
  };

  // Open only fatture estere for a specific user/year/month
  const openFattureEstereDocuments = async (item) => {
    // Create a mock user object for the modal
    const mockUser = {
      user: {
        id: item.user_id,
        name: item.user_name,
        email: item.user_email
      }
    };
    setSelectedUser(mockUser);
    setLoadingDocs(true);
    setViewingFattureEstereOnly(true); // Viewing only fatture estere
    
    // Set the selected year/month to match the item
    setSelectedYear(item.year);
    setSelectedMonth(item.month);
    
    try {
      const response = await axios.get(
        `${API}/admin/users/${item.user_id}/documents/${item.year}/${item.month}`,
        { headers: getAuthHeader() }
      );
      // Filter only fatture_estere category
      const fattureEstere = response.data.filter(doc => doc.category === "fatture_estere");
      setUserDocuments(fattureEstere);
    } catch (error) {
      toast.error("Errore nel caricamento documenti");
    } finally {
      setLoadingDocs(false);
    }
  };

  const toggleDocumentRegistered = async (docId) => {
    try {
      const response = await axios.put(
        `${API}/admin/documents/${docId}/register`,
        {},
        { headers: getAuthHeader() }
      );
      
      setUserDocuments(prev => prev.map(doc => 
        doc.id === docId 
          ? { ...doc, registered: response.data.registered, registered_at: response.data.registered_at }
          : doc
      ));
      
      fetchDashboardData();
      fetchFattureEsterePending(); // Refresh fatture estere pending
      toast.success(response.data.registered ? "Documento registrato" : "Registrazione rimossa");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDownload = async (docId, filename, autoRegister = true) => {
    try {
      const response = await axios.get(`${API}/documents/download/${docId}`, {
        headers: getAuthHeader()
      });
      
      const { file_data, file_type } = response.data;
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Auto-register document after download
      if (autoRegister) {
        const doc = userDocuments.find(d => d.id === docId);
        if (doc && !doc.registered) {
          await axios.put(
            `${API}/admin/documents/${docId}/register`,
            {},
            { headers: getAuthHeader() }
          );
          
          setUserDocuments(prev => prev.map(d => 
            d.id === docId 
              ? { ...d, registered: true, registered_at: new Date().toISOString() }
              : d
          ));
          
          toast.success("Documento scaricato e registrato");
        }
      }
    } catch (error) {
      toast.error("Errore nel download");
    }
  };

  const handleDownloadAllDocs = async () => {
    if (!selectedUser || userDocuments.length === 0) return;
    
    setDownloadingAll(true);
    toast.info(`Scaricamento di ${userDocuments.length} documenti...`);
    
    let registeredCount = 0;
    
    try {
      for (const doc of userDocuments) {
        const response = await axios.get(`${API}/documents/download/${doc.id}`, {
          headers: getAuthHeader()
        });
        
        const { file_data, file_type } = response.data;
        const byteCharacters = atob(file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: file_type });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // Add client name and month to filename for clarity
        link.download = `${selectedUser.user.name}_${MONTHS[selectedMonth - 1]}_${doc.filename}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        // Auto-register if not already registered
        if (!doc.registered) {
          try {
            await axios.put(
              `${API}/admin/documents/${doc.id}/register`,
              {},
              { headers: getAuthHeader() }
            );
            registeredCount++;
          } catch (err) {
            console.error("Error registering doc:", doc.id);
          }
        }
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Update local state
      setUserDocuments(prev => prev.map(d => ({
        ...d,
        registered: true,
        registered_at: d.registered_at || new Date().toISOString()
      })));
      
      fetchDashboardData();
      fetchFattureEsterePending(); // Refresh fatture estere pending
      
      toast.success(`Scaricati ${userDocuments.length} documenti e registrati ${registeredCount} nuovi!`);
    } catch (error) {
      toast.error("Errore nel download");
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadMonthZip = async () => {
    setDownloadingMonthZip(true);
    toast.info(`Preparazione ZIP per ${MONTHS[selectedMonth - 1]} ${selectedYear}...`);
    
    try {
      const response = await axios.get(
        `${API}/admin/download-month/${selectedYear}/${selectedMonth}?auto_register=true`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Documenti_${MONTHS[selectedMonth - 1]}_${selectedYear}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Refresh data to show updated registration status
      fetchDashboardData();
      fetchFattureEsterePending(); // Refresh fatture estere pending
      
      toast.success(`ZIP scaricato! Tutti i documenti sono stati registrati.`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Nessun documento trovato per questo periodo");
      } else {
        toast.error("Errore nel download");
      }
    } finally {
      setDownloadingMonthZip(false);
    }
  };

  const handleDeleteSingleDoc = async () => {
    if (!deleteDocDialog.doc) return;
    
    setDeletingDoc(true);
    try {
      await axios.delete(`${API}/documents/${deleteDocDialog.doc.id}`, {
        headers: getAuthHeader()
      });
      
      toast.success("Documento eliminato");
      setDeleteDocDialog({ open: false, doc: null });
      
      // Refresh documents list
      setUserDocuments(prev => prev.filter(d => d.id !== deleteDocDialog.doc.id));
      fetchDashboardData();
      fetchStorageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeletingDoc(false);
    }
  };

  const handleDeletePeriod = async () => {
    if (!deleteDialog.year) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/documents/bulk`, {
        headers: getAuthHeader(),
        data: {
          year: deleteDialog.year,
          month: deleteDialog.month
        }
      });
      
      toast.success(response.data.message);
      setDeleteDialog({ open: false, year: null, month: null });
      fetchDashboardData();
      fetchStorageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const getWarningColor = (level) => {
    switch (level) {
      case "critical": return "text-red-500";
      case "warning": return "text-yellow-500";
      default: return "text-[#10B981]";
    }
  };

  const getWarningBg = (level) => {
    switch (level) {
      case "critical": return "bg-red-500/10 border-red-500/30";
      case "warning": return "bg-yellow-500/10 border-yellow-500/30";
      default: return "bg-[#10B981]/10 border-[#10B981]/30";
    }
  };

  const totalUsers = usersStats.length;
  const usersWithDocs = usersStats.filter(u => u.total_documents > 0).length;
  const totalDocs = usersStats.reduce((sum, u) => sum + u.total_documents, 0);
  const registeredDocs = usersStats.reduce((sum, u) => sum + u.registered_documents, 0);

  // Get available years from storage stats for deletion
  const availableYears = storageStats ? Object.keys(storageStats.size_by_year).map(Number).sort((a, b) => b - a) : [];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-[#A1A1AA] hover:text-[#D4AF37] -ml-2 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-2xl font-serif text-[#EDEDED]">Dashboard Amministratore</h1>
              <p className="text-[#52525B] text-sm">Panoramica caricamenti clienti</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Fatture Estere Pending Section */}
        {!loadingFattureEstere && fattureEsterePending && fattureEsterePending.total_pending_documents > 0 && (
          <div className="mb-6 bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-lg overflow-hidden">
            <button
              onClick={() => setFattureEstereExpanded(!fattureEstereExpanded)}
              className="w-full p-4 flex items-center justify-between hover:bg-[#D4AF37]/5 transition-colors"
              data-testid="fatture-estere-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-medium text-[#EDEDED] flex items-center gap-2">
                    Fatture Estere da Registrare
                    <Badge className="bg-[#D4AF37] text-black border-0">
                      {fattureEsterePending.total_pending_documents}
                    </Badge>
                  </h2>
                  <p className="text-[#A1A1AA] text-sm">
                    {fattureEsterePending.total_users_with_pending} {fattureEsterePending.total_users_with_pending === 1 ? 'cliente ha' : 'clienti hanno'} fatture estere non spuntate
                  </p>
                </div>
              </div>
              {fattureEstereExpanded ? (
                <ChevronUp className="w-5 h-5 text-[#A1A1AA]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#A1A1AA]" />
              )}
            </button>
            
            {fattureEstereExpanded && (
              <div className="px-4 pb-4">
                <div className="bg-[#0A0A0A] rounded-lg border border-neutral-800 divide-y divide-neutral-800">
                  {fattureEsterePending.items.map((item, idx) => (
                    <div 
                      key={`${item.user_id}-${item.year}-${item.month}`}
                      className="p-4 hover:bg-neutral-900/50 transition-colors"
                      data-testid={`fattura-estera-item-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center">
                            <span className="text-[#D4AF37] font-semibold text-sm">
                              {item.user_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-[#EDEDED] font-medium">{item.user_name}</p>
                            <p className="text-[#52525B] text-xs">{item.user_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-[#EDEDED] font-mono text-sm">
                              {item.month_name} {item.year}
                            </p>
                            <p className="text-[#D4AF37] text-xs font-medium">
                              {item.document_count} {item.document_count === 1 ? 'documento' : 'documenti'}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openFattureEstereDocuments(item)}
                            className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            data-testid={`view-fattura-${idx}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Vedi
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Storage Alert */}
        {storageStats && storageStats.warning_level !== "ok" && (
          <div className={`mb-6 p-4 rounded-lg border ${getWarningBg(storageStats.warning_level)}`}>
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-5 h-5 ${getWarningColor(storageStats.warning_level)}`} />
              <div>
                <p className={`font-medium ${getWarningColor(storageStats.warning_level)}`}>
                  {storageStats.warning_level === "critical" ? "Spazio critico!" : "Attenzione spazio"}
                </p>
                <p className="text-sm text-[#A1A1AA]">
                  Stai usando {storageStats.total_size_mb} MB. Considera di eliminare documenti vecchi.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Storage Stats Card */}
        <div className="mb-8 bg-[#0A0A0A] border border-neutral-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <HardDrive className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-lg font-medium text-[#EDEDED]">Spazio di Archiviazione</h2>
            </div>
            {loadingStorage ? (
              <div className="w-5 h-5 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Badge className={`${getWarningBg(storageStats?.warning_level)} ${getWarningColor(storageStats?.warning_level)} border`}>
                {storageStats?.warning_level === "ok" ? "OK" : storageStats?.warning_level === "warning" ? "Attenzione" : "Critico"}
              </Badge>
            )}
          </div>
          
          {!loadingStorage && storageStats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Spazio Totale</p>
                  <p className={`text-2xl font-mono ${getWarningColor(storageStats.warning_level)}`}>
                    {storageStats.total_size_mb} MB
                  </p>
                </div>
                <div>
                  <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Documenti Totali</p>
                  <p className="text-2xl font-mono text-[#EDEDED]">{storageStats.total_documents}</p>
                </div>
                <div>
                  <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Limite Consigliato</p>
                  <p className="text-2xl font-mono text-[#A1A1AA]">10 GB</p>
                </div>
                <div>
                  <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Utilizzo</p>
                  <Progress 
                    value={Math.min((storageStats.total_size_mb / 10000) * 100, 100)} 
                    className="h-2 bg-neutral-800 mt-2"
                  />
                </div>
              </div>

              {/* Size by Year */}
              <div className="border-t border-neutral-800 pt-4">
                <p className="text-[#A1A1AA] text-sm mb-3">Spazio per Anno:</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(storageStats.size_by_year)
                    .sort(([a], [b]) => Number(b) - Number(a))
                    .map(([year, size]) => (
                      <div 
                        key={year}
                        className="flex items-center gap-2 bg-neutral-900 px-3 py-2 rounded"
                      >
                        <span className="text-[#EDEDED] font-mono">{year}</span>
                        <span className="text-[#52525B]">→</span>
                        <span className="text-[#D4AF37] font-mono">{size} MB</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, year: Number(year), month: null })}
                          className="text-[#A1A1AA] hover:text-red-500 h-6 w-6 p-0 ml-1"
                          title={`Elimina tutti i documenti del ${year}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-32 bg-[#0A0A0A] border-neutral-800">
              <SelectValue placeholder="Anno" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-neutral-800">
              {validYears.map(year => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-40 bg-[#0A0A0A] border-neutral-800">
              <SelectValue placeholder="Mese" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-neutral-800">
              {MONTHS.map((month, idx) => (
                <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            onClick={() => setDeleteDialog({ open: true, year: selectedYear, month: selectedMonth })}
            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Elimina {MONTHS[selectedMonth - 1]} {selectedYear}
          </Button>

          <Button
            onClick={handleDownloadMonthZip}
            disabled={downloadingMonthZip || totalDocs === 0}
            className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
            data-testid="download-month-zip-btn"
          >
            {downloadingMonthZip ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <FolderDown className="w-4 h-4 mr-2" />
            )}
            Scarica Tutti ({totalDocs} doc)
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0A0A0A] border border-neutral-800 p-4 rounded-lg">
            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Utenti Totali</p>
            <p className="text-2xl font-mono text-[#EDEDED]">{totalUsers}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-neutral-800 p-4 rounded-lg">
            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Con Documenti</p>
            <p className="text-2xl font-mono text-[#D4AF37]">{usersWithDocs}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-neutral-800 p-4 rounded-lg">
            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Documenti Totali</p>
            <p className="text-2xl font-mono text-[#EDEDED]">{totalDocs}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-neutral-800 p-4 rounded-lg">
            <p className="text-[#52525B] text-xs uppercase tracking-wider mb-1">Registrati</p>
            <p className="text-2xl font-mono text-[#10B981]">{registeredDocs}</p>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-neutral-800 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Cliente</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Categorie</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Documenti</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Registrati</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs">Stato</TableHead>
                  <TableHead className="text-[#A1A1AA] uppercase tracking-wider text-xs text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usersStats.map((stat) => {
                  const progress = stat.total_categories > 0 
                    ? (stat.categories_with_docs / stat.total_categories) * 100 
                    : 0;
                  const allRegistered = stat.total_documents > 0 && stat.registered_documents === stat.total_documents;
                  
                  return (
                    <TableRow 
                      key={stat.user.id} 
                      className="border-neutral-800 hover:bg-neutral-900/50"
                      data-testid={`user-stat-${stat.user.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="text-[#EDEDED] font-medium">{stat.user.name}</p>
                          <p className="text-[#52525B] text-xs">{stat.user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={progress} 
                            className="w-20 h-2 bg-neutral-800"
                          />
                          <span className="text-xs text-[#A1A1AA] font-mono">
                            {stat.categories_with_docs}/{stat.total_categories}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-[#EDEDED]">{stat.total_documents}</span>
                      </TableCell>
                      <TableCell>
                        <span className={`font-mono ${allRegistered ? 'text-[#10B981]' : 'text-[#D4AF37]'}`}>
                          {stat.registered_documents}
                        </span>
                      </TableCell>
                      <TableCell>
                        {stat.total_documents === 0 ? (
                          <Badge variant="secondary" className="bg-neutral-800 text-neutral-400">
                            Nessun doc
                          </Badge>
                        ) : allRegistered ? (
                          <Badge className="bg-[#10B981]/20 text-[#10B981] border-0">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Completo
                          </Badge>
                        ) : (
                          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-0">
                            <Clock className="w-3 h-3 mr-1" />
                            In attesa
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openUserDocuments(stat)}
                          className="text-[#A1A1AA] hover:text-[#D4AF37]"
                          disabled={stat.total_documents === 0}
                          data-testid={`view-docs-${stat.user.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Dettagli
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      {/* Document Detail Modal */}
      <Dialog open={selectedUser !== null} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#D4AF37]" />
                  {viewingFattureEstereOnly ? "Fatture Estere di " : "Documenti di "}{selectedUser?.user.name}
                </DialogTitle>
                <p className="text-[#52525B] text-sm mt-1">
                  {MONTHS[selectedMonth - 1]} {selectedYear}
                  {viewingFattureEstereOnly && <span className="text-[#D4AF37] ml-2">• Solo Fatture Estere</span>}
                </p>
              </div>
              {userDocuments.length > 0 && (
                <Button
                  onClick={handleDownloadAllDocs}
                  disabled={downloadingAll}
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
                  data-testid="download-all-docs-btn"
                >
                  {downloadingAll ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <FolderDown className="w-4 h-4 mr-2" />
                  )}
                  Scarica Tutti ({userDocuments.length})
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {loadingDocs ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : userDocuments.length === 0 ? (
            <p className="text-center text-[#52525B] py-8">Nessun documento caricato</p>
          ) : (
            <div className="space-y-3 mt-4">
              {userDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className={`
                    p-4 rounded-lg border transition-colors
                    ${doc.registered 
                      ? 'bg-[#10B981]/5 border-[#10B981]/30' 
                      : 'bg-neutral-900/50 border-neutral-800'}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={doc.registered}
                        onCheckedChange={() => toggleDocumentRegistered(doc.id)}
                        className="border-[#D4AF37] data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                        data-testid={`register-checkbox-${doc.id}`}
                      />
                      <div>
                        <p className="text-[#EDEDED] text-sm font-medium">{doc.filename}</p>
                        <p className="text-[#52525B] text-xs">
                          {doc.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          <span className="mx-2">•</span>
                          {new Date(doc.uploaded_at).toLocaleDateString("it-IT")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.registered && (
                        <Badge className="bg-[#10B981]/20 text-[#10B981] border-0 text-xs">
                          Registrato
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc.id, doc.filename)}
                        className="text-[#A1A1AA] hover:text-[#D4AF37] h-8 w-8 p-0"
                        title="Scarica documento"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteDocDialog({ open: true, doc })}
                        className="text-[#A1A1AA] hover:text-red-500 h-8 w-8 p-0"
                        title="Elimina documento"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Conferma Eliminazione
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {deleteDialog.month ? (
                <>
                  Sei sicuro di voler eliminare <strong>tutti i documenti</strong> di{" "}
                  <strong>{MONTHS[deleteDialog.month - 1]} {deleteDialog.year}</strong>?
                </>
              ) : (
                <>
                  Sei sicuro di voler eliminare <strong>tutti i documenti</strong> dell'anno{" "}
                  <strong>{deleteDialog.year}</strong>?
                </>
              )}
              <br /><br />
              <span className="text-red-400">Questa azione non può essere annullata. Assicurati di aver fatto un backup prima.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteDialog({ open: false, year: null, month: null })}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleDeletePeriod}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Single Document Confirmation Dialog */}
      <Dialog open={deleteDocDialog.open} onOpenChange={(open) => setDeleteDocDialog({ ...deleteDocDialog, open })}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Conferma Eliminazione Documento
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Sei sicuro di voler eliminare il documento <strong>"{deleteDocDialog.doc?.filename}"</strong>?
              <br /><br />
              <span className="text-red-400">Questa azione non può essere annullata.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteDocDialog({ open: false, doc: null })}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleDeleteSingleDoc}
              disabled={deletingDoc}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-doc-btn"
            >
              {deletingDoc ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
